/**
 * The bonus stacking engine — port of `Bonus.Int` from COMP/CON
 * (`reference/compcon/src/classes/components/feature/bonus/Bonus.ts:135`) and
 * `reference/compcon-cli/src/lib/applyBonuses.ts`, reconciled to the canonical
 * COMP/CON three-mode semantics (the cli only implemented two of the three).
 *
 * A bonus targets a stat (`id`, e.g. `"hp"`, `"evasion"`) and carries a value
 * (a number or a formula string like `"{grit}"`). Three stacking modes per id:
 *
 *   - **overwrite** (`overwrite: true`, not `replace`): ignore the base stat and
 *     every other bonus — the stat becomes `max(overwrite values)`.
 *   - **replace** (`replace: true`): the *sum* of replace bonuses *becomes the
 *     new base* (displacing the frame/HASE base), then additive bonuses stack on
 *     top of that.
 *   - **additive** (neither flag): summed on top of the base (or the replaced
 *     base, if any).
 *
 * Precedence: overwrite > replace > additive, exactly as `Bonus.Int`.
 *
 * Pure functions over plain data; no command/store deps (the dependency graph
 * keeps `rules` from ever importing command machinery).
 */

import { evaluateModifier, type ModifierValue, type ModifierVars } from "./evaluateModifier";

/**
 * A bonus as it appears embedded on compendium items (`frame.bonuses`,
 * `system.bonuses`, talent ranks, …). The compendium carries these verbatim via
 * `.passthrough()` as untyped content, so this is the engine's own input
 * contract for the subset of fields it reads. `id` is the stat key the bonus
 * targets (see {@link StatBonusId}); the rest are the stacking flags.
 */
export type Bonus = {
  id: string;
  /**
   * Absent on an accuracy-only bonus, such as the Goblin's Liturgicode (`{id: "tech_attack",
   * accuracy: 1}`) or Gyges Frame's `{id: "hull", accuracy: 1}`. It shifts the Accuracy/Difficulty
   * of a roll, not the stat's number, so {@link applyBonus} skips it. Each one applies at roll time
   * through a core-rulebook overlay entry under its carrier's id (`goblin_liturgicode`,
   * `cb_gyges_frame`, …); `encounter/spawn/accuracyBonusCoverage.test.ts` fails when one has none.
   */
  val?: ModifierValue;
  overwrite?: boolean;
  replace?: boolean;
}

/**
 * Apply all bonuses targeting `id` to a base value, with overwrite/replace/
 * additive stacking. `vars` supplies the formula context for string values.
 *
 * Mirrors `Bonus.Int(base, id, source)`:
 *   1. overwrite bonuses (and not replace) → return their max, ignoring base.
 *   2. else replace bonuses → their sum becomes the working base.
 *   3. additive bonuses → summed onto the working base.
 */
type ApplyBonusArgs = {
  id: string;
  base: number;
  bonuses: Bonus[];
  vars: ModifierVars;
};

export function applyBonus(args: ApplyBonusArgs): number {
  const { id, base, bonuses, vars } = args;
  // A bonus with no `val` (accuracy-only) never touches the stat's number.
  const evals = bonuses.flatMap((b) =>
    b.id === id && b.val != null ? [{ b, val: evaluateModifier(b.val, vars) }] : [],
  );
  if (evals.length === 0) return base;

  // 1. overwrite (and not replace): max, ignoring base + everything else.
  const overwrites = evals.filter(({ b }) => b.overwrite && !b.replace);
  if (overwrites.length > 0) {
    return Math.max(...overwrites.map((e) => e.val));
  }

  // 2. replace: sum becomes the new base (displacing `base`).
  const replaces = evals.filter(({ b }) => b.replace);
  const workingBase =
    replaces.length > 0 ? replaces.reduce((sum, e) => sum + e.val, 0) : base;

  // 3. additive: sum onto the working base.
  const additive = evals
    .filter(({ b }) => !b.overwrite && !b.replace)
    .reduce((sum, e) => sum + e.val, 0);

  return workingBase + additive;
}

/**
 * Build a reusable applier bound to a fixed bonus set + variable context — the
 * `getApplyBonus` shape from the cli. Lets a selector apply many stats against
 * one bonus list without re-passing it each call.
 */
export function makeBonusApplier(
  bonuses: Bonus[],
  vars: ModifierVars,
): (id: string, base: number) => number {
  return (id, base) => applyBonus({ id, base, bonuses, vars });
}

/** Re-export the formula evaluator pieces so consumers get one import surface. */
export { evaluateModifier };
export type { ModifierValue, ModifierVars };
